import { useCallback, useMemo, useRef, useState } from "react";

/** Multi-select state over flow IDs (focus-view batch actions). */
export interface FlowSelection {
  selectedIds: string[];
  isSelected: (flowId: string) => boolean;
  hasSelection: boolean;
  selectedCount: number;
  toggle: (flowId: string) => void;
  selectRange: (flowId: string) => void;
  selectAll: (flowIds: string[]) => void;
  setOrderedIds: (flowIds: string[]) => void;
  clear: () => void;
}

export function useFlowSelection(): FlowSelection {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const rangeAnchorRef = useRef<string | null>(null);
  const orderedIdsRef = useRef<string[]>([]);

  const toggle = useCallback((flowId: string) => {
    rangeAnchorRef.current = flowId;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(flowId)) next.delete(flowId);
      else next.add(flowId);
      return next;
    });
  }, []);

  const selectRange = useCallback((flowId: string) => {
    const orderedIds = orderedIdsRef.current;
    const anchor = rangeAnchorRef.current;
    const anchorIndex = anchor ? orderedIds.indexOf(anchor) : -1;
    const targetIndex = orderedIds.indexOf(flowId);

    rangeAnchorRef.current = flowId;
    if (anchorIndex === -1 || targetIndex === -1) {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(flowId)) next.delete(flowId);
        else next.add(flowId);
        return next;
      });
      return;
    }

    const [start, end] =
      anchorIndex <= targetIndex
        ? [anchorIndex, targetIndex]
        : [targetIndex, anchorIndex];
    setSelected((prev) => {
      const next = new Set(prev);
      for (let index = start; index <= end; index += 1) {
        next.add(orderedIds[index]);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback((flowIds: string[]) => {
    rangeAnchorRef.current = null;
    setSelected(new Set(flowIds));
  }, []);

  const setOrderedIds = useCallback((flowIds: string[]) => {
    orderedIdsRef.current = flowIds;
  }, []);

  const clear = useCallback(() => {
    rangeAnchorRef.current = null;
    setSelected(new Set());
  }, []);

  const isSelected = useCallback(
    (flowId: string) => selected.has(flowId),
    [selected],
  );

  return useMemo(
    () => ({
      selectedIds: [...selected],
      isSelected,
      hasSelection: selected.size > 0,
      selectedCount: selected.size,
      toggle,
      selectRange,
      selectAll,
      setOrderedIds,
      clear,
    }),
    [
      selected,
      isSelected,
      toggle,
      selectRange,
      selectAll,
      setOrderedIds,
      clear,
    ],
  );
}
